import type { OrgMemberListItem, OrgMemberRole } from "@solomon-apps/shared";
import { ContentBlock } from "@solomon-apps/shared/src/components/console";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import {
    CheckIcon,
    CrossIcon,
    PencilPaperIcon
} from "@solomon-labs/design/components/icons";
import {
    Button,
    ButtonIcon,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import { motion, useReducedMotion } from "motion/react";
import React, { useState } from "react";

import { DisabledReason } from "@/components/DisabledReason";
import { LabeledInput, ReadOnlyField } from "@/pages/shared/LabeledField";

import { RoleChoiceField } from "./RoleChoiceField";

export interface MemberAccountSectionProps {
    member: OrgMemberListItem;
    /** The signed-in user is an Owner (only Owners manage the team). */
    viewerIsOwner: boolean;
    /** Name is editable here (an Owner, or the member themselves). */
    canEditName: boolean;
    /** Why the role cannot change, on demand; null = editable. */
    roleLockReason: string | null;
    /** Why Suspend / Reactivate is unavailable, on demand; null = available. */
    statusLockReason: string | null;
    /** A mutation is in flight; every action holds still until it settles. */
    isMutating: boolean;
    onSaveName: (fullName: string) => Promise<void>;
    onSaveRole: (role: OrgMemberRole) => Promise<void>;
    onToggleStatus: () => void;
    onEditInvite: () => void;
    onResendInvitation: () => void;
    onWithdrawInvitation: () => void;
}

/**
 * The member page's left column, the same construction as Profile's Account
 * section: label-inside light-bar fields (Name, Email, Role), each
 * editable one carrying the pencil at its inline end, then the member's
 * access action.
 *
 *   - Active / Suspended: Name and Role edit in place (the Profile Name
 *     interaction: pencil, then save and cancel). Email never changes once an
 *     account is claimed, so it has no pencil. A locked Role keeps its pencil,
 *     disabled, with the reason on demand.
 *   - Invited: every pencil opens Edit Invitation (fix-invite), which edits
 *     email, name and role together and re-issues the link.
 *
 * Outcomes live in the controls, never in standing copy: a failed save
 * marks the field invalid and its save button carries the reason on demand.
 */
export function MemberAccountSection({
    member,
    viewerIsOwner,
    canEditName,
    roleLockReason,
    statusLockReason,
    isMutating,
    onSaveName,
    onSaveRole,
    onToggleStatus,
    onEditInvite,
    onResendInvitation,
    onWithdrawInvitation
}: MemberAccountSectionProps): React.ReactElement {
    const invited = member.status === "Invited";
    const invitePencil = (label: string): React.ReactNode =>
        invited && viewerIsOwner ? (
            <PencilButton
                label={`Edit invitation ${label.toLowerCase()}`}
                onClick={onEditInvite}
                disabled={isMutating}
            />
        ) : undefined;

    return (
        <ContentBlock title="Account" className="px-4 md:px-6 gap-1 md:gap-2">
            <div className="flex flex-col gap-3 py-4">
                {invited ? (
                    <ReadOnlyField
                        label="Name"
                        value={member.fullName ?? ""}
                        actions={invitePencil("Name")}
                    />
                ) : (
                    <NameField
                        // Reset the draft when another member's page opens.
                        key={member.uuid}
                        fullName={member.fullName}
                        canEdit={canEditName}
                        isMutating={isMutating}
                        onSave={onSaveName}
                    />
                )}
                <ReadOnlyField
                    label="Email"
                    value={member.email}
                    actions={invitePencil("Email")}
                />
                {invited ? (
                    <ReadOnlyField
                        label="Role"
                        value={member.orgRole}
                        actions={invitePencil("Role")}
                    />
                ) : (
                    <RoleField
                        key={member.uuid}
                        role={member.orgRole}
                        viewerIsOwner={viewerIsOwner}
                        lockReason={roleLockReason}
                        isMutating={isMutating}
                        onSave={onSaveRole}
                    />
                )}
            </div>

            {/* The member's access action, Owners only (a Member manages
                nobody). No Delete: the console has no remove-member call. */}
            {viewerIsOwner && (
                <div className="flex flex-wrap items-center gap-4 py-3">
                    {invited ? (
                        <>
                            <Button
                                variant="raised"
                                interaction="lifted"
                                size="md"
                                onClick={onResendInvitation}
                                disabled={isMutating}
                            >
                                Resend Invitation
                            </Button>
                            <Button
                                variant="link-error"
                                interaction="underline"
                                size="sm"
                                onClick={onWithdrawInvitation}
                                disabled={isMutating}
                            >
                                Withdraw Invitation
                            </Button>
                        </>
                    ) : (
                        <DisabledReason
                            reason={statusLockReason}
                            label={
                                member.status === "Suspended"
                                    ? "Reactivate"
                                    : "Suspend"
                            }
                        >
                            <Button
                                variant="raised"
                                interaction="lifted"
                                size="md"
                                onClick={onToggleStatus}
                                disabled={
                                    statusLockReason !== null || isMutating
                                }
                                // Suspend and Reactivate share one button; the
                                // label cross-fades when the status flips.
                                labels={STATUS_LABELS}
                                labelKey={
                                    member.status === "Suspended"
                                        ? "reactivate"
                                        : "suspend"
                                }
                            />
                        </DisabledReason>
                    )}
                </div>
            )}
        </ContentBlock>
    );
}

const STATUS_LABELS = { suspend: "Suspend", reactivate: "Reactivate" };

/* ── Inline edit ──────────────────────────────────────────── */

/**
 * The read / edit swap fades the incoming field in: opacity only, 200ms
 * ease-out-quad (the console's arrival fade), skipped under reduced motion.
 * The field's first render does not fade: it arrives with the page enter.
 */
function useSwapFade(): {
    fadeIn: React.ComponentProps<typeof motion.div>;
    setEditing: (next: boolean) => void;
    editing: boolean;
} {
    const reducedMotion = useReducedMotion() ?? false;
    const [editing, setEditingState] = useState(false);
    const [swapped, setSwapped] = useState(false);
    const setEditing = (next: boolean): void => {
        setSwapped(true);
        setEditingState(next);
    };
    const fadeIn: React.ComponentProps<typeof motion.div> =
        reducedMotion || !swapped
            ? { initial: false }
            : {
                  initial: { opacity: 0 },
                  animate: { opacity: 1 },
                  transition: { duration: 0.2, ease: easings.outQuad }
              };
    return { fadeIn, editing, setEditing };
}

function PencilButton({
    label,
    onClick,
    disabled = false
}: {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
}): React.ReactElement {
    return (
        <ButtonIcon
            variant="transparent"
            size="lg"
            icon={<PencilPaperIcon />}
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
        />
    );
}

/**
 * Save and cancel at the field's inline end. After a failed save the save
 * button reads "Retry" to assistive tech and carries the reason on hover or
 * focus; there is no sentence under the field.
 */
function EditActions({
    noun,
    error,
    saveDisabled,
    busy,
    onSave,
    onCancel
}: {
    noun: string;
    error: Error | null;
    saveDisabled: boolean;
    busy: boolean;
    onSave: () => void;
    onCancel: () => void;
}): React.ReactElement {
    const save = (
        <ButtonIcon
            variant="transparent"
            size="lg"
            icon={<CheckIcon />}
            aria-label={
                error
                    ? `Retry saving ${noun}: ${error.message}`
                    : `Save ${noun}`
            }
            disabled={saveDisabled || busy}
            onClick={onSave}
        />
    );
    return (
        <>
            <TooltipProvider>
                <Tooltip disabled={!error}>
                    <TooltipTrigger render={save} />
                    <TooltipContent side="top">{error?.message}</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <ButtonIcon
                variant="transparent"
                size="lg"
                icon={<CrossIcon />}
                aria-label={`Cancel editing ${noun}`}
                disabled={busy}
                onClick={onCancel}
            />
        </>
    );
}

function NameField({
    fullName,
    canEdit,
    isMutating,
    onSave
}: {
    fullName: string | null;
    canEdit: boolean;
    isMutating: boolean;
    onSave: (fullName: string) => Promise<void>;
}): React.ReactElement {
    const [draft, setDraft] = useState(fullName ?? "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { fadeIn, editing, setEditing } = useSwapFade();

    const cancel = (): void => {
        setDraft(fullName ?? "");
        setError(null);
        setEditing(false);
    };

    const save = async (): Promise<void> => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        if (trimmed === (fullName ?? "")) {
            cancel();
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave(trimmed);
            setEditing(false);
        } catch (caught) {
            setError(
                caught instanceof Error ? caught : new Error(String(caught))
            );
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        return (
            <motion.div key="read" {...fadeIn}>
                <ReadOnlyField
                    label="Name"
                    value={fullName ?? ""}
                    actions={
                        canEdit ? (
                            <PencilButton
                                label="Edit name"
                                disabled={isMutating}
                                onClick={() => {
                                    setDraft(fullName ?? "");
                                    setEditing(true);
                                }}
                            />
                        ) : undefined
                    }
                />
            </motion.div>
        );
    }

    return (
        <motion.div key="edit" {...fadeIn}>
            <LabeledInput
                label="Name"
                type="text"
                value={draft}
                onChange={(e) => {
                    setDraft(e.target.value);
                    setError(null);
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault();
                        void save();
                    } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancel();
                    }
                }}
                disabled={saving}
                aria-invalid={error ? true : undefined}
                aria-label="Name"
                autoFocus
                actions={
                    <EditActions
                        noun="name"
                        error={error}
                        saveDisabled={!draft.trim()}
                        busy={saving}
                        onSave={() => void save()}
                        onCancel={cancel}
                    />
                }
            />
        </motion.div>
    );
}

function RoleField({
    role,
    viewerIsOwner,
    lockReason,
    isMutating,
    onSave
}: {
    role: OrgMemberRole;
    viewerIsOwner: boolean;
    lockReason: string | null;
    isMutating: boolean;
    onSave: (role: OrgMemberRole) => Promise<void>;
}): React.ReactElement {
    const [draft, setDraft] = useState<OrgMemberRole>(role);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { fadeIn, editing, setEditing } = useSwapFade();

    const cancel = (): void => {
        setDraft(role);
        setError(null);
        setEditing(false);
    };

    const save = async (): Promise<void> => {
        if (draft === role) {
            cancel();
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await onSave(draft);
            setEditing(false);
        } catch (caught) {
            setError(
                caught instanceof Error ? caught : new Error(String(caught))
            );
        } finally {
            setSaving(false);
        }
    };

    if (!editing) {
        // A Member viewer edits nobody's role, so no pencil at all; an Owner
        // facing a locked role (the last Owner, themselves) sees it disabled
        // with the reason on demand.
        const pencil = viewerIsOwner ? (
            <DisabledReason reason={lockReason} label="Edit role">
                <PencilButton
                    label="Edit role"
                    disabled={lockReason !== null || isMutating}
                    onClick={() => {
                        setDraft(role);
                        setEditing(true);
                    }}
                />
            </DisabledReason>
        ) : undefined;
        return (
            <motion.div key="read" {...fadeIn}>
                <ReadOnlyField label="Role" value={role} actions={pencil} />
            </motion.div>
        );
    }

    return (
        <motion.div key="edit" {...fadeIn}>
            {/* The role choice sits where the value was; focus lands on the
                held role when editing opens, as the Name field's input does. */}
            <RoleChoiceField
                value={draft}
                onChange={(next) => {
                    if (saving) return;
                    setDraft(next);
                    setError(null);
                }}
                disabled={saving}
                autoFocus
                actions={
                    <EditActions
                        noun="role"
                        error={error}
                        saveDisabled={draft === role}
                        busy={saving}
                        onSave={() => void save()}
                        onCancel={cancel}
                    />
                }
            />
        </motion.div>
    );
}
